import type { DrawContext } from './draw';
import type { View } from './project';
import { toScreen } from './project';

/*
 * THE RUNGS, AND THE LIGHT AT THE CENTRE — the ladder the whole map is read against.
 *
 * Ported from `reference/universe-v8/engine.js` — `drawStatic` lines 1403–1427.
 *
 * Radius means nearness to the divine, and that claim is invisible unless the rungs themselves are
 * drawn. Each is one dashed ellipse; the middle carries a nine-stop glow that says where the ladder
 * starts. Both are deliberately faint, and get fainter as the reader comes closer — the rungs are
 * scaffolding for reading positions, not a thing to look at, so they show most when the reader is
 * far enough out that position is ALL they can see.
 *
 * DASHED, NOT SOLID, and their `[2, 8]` is doing real work: a solid ring reads as a boundary, and a
 * boundary invites the question of which side a figure is on. A dotted one reads as a gradation.
 *
 * THE RUNGS ARE DATA — six of theirs, five of ours — so the radii arrive as an argument and this
 * file never counts them. The colours and the alpha steps are the LOOK, and stay.
 *
 * (Their line 1403 is `this.tboxes=[]`, which resets the label space rather than drawing anything.
 * That belongs to the frame, not to this layer, and `LabelSpace` in `labels.ts` already owns it.)
 */

/** Their turn, in radians, exactly as written in the reference. */
const TAU = 6.2832;

/** One rung: how far out it sits. Everything else about it is the look. */
export interface Rung {
  r: number;
}

/** The world dimensions this layer needs. Both arguments, neither with a default. */
export interface RungWorld {
  /** The rungs, outward. Theirs are six; ours are five. */
  rungs: readonly Rung[];
  /**
   * How far the glow at the centre reaches, in world units.
   *
   * Theirs is 300, which sits just outside their innermost rung. It is a WORLD radius — it scales
   * with the zoom — so a different ladder needs a different one.
   */
  coreRadius: number;
  /** The world's vertical scale. */
  squash: number;
}

/**
 * Below this on screen a rung is a smudge around the origin.
 *
 * A screen threshold rather than a world one, and therefore mechanism: it is the point at which an
 * ellipse stops being legible as a ring, which is a fact about eyes and not about the ladder.
 */
const RUNG_MIN_PX = 26;

/** Their dash: two on, eight off. */
const RUNG_DASH: readonly number[] = [2, 8];

/** Solid again. Restored after each rung, so nothing drawn later inherits the dash. */
const NO_DASH: readonly number[] = [];

/** The smallest the core glow is allowed to be on screen, so it never vanishes entirely. */
const CORE_MIN_PX = 70;

/**
 * How strongly the rungs show, by how far back the reader is.
 *
 * Three steps rather than a ramp, and that is deliberate: a continuous fade would have the rungs
 * always slightly present, and the point is that they recede to almost nothing once the reader is
 * close enough to be reading names instead of positions.
 */
function rungAlpha(zoom: number): number {
  if (zoom < 0.12) return 0.3;
  if (zoom < 0.3) return 0.2;
  return 0.08;
}

/** The nine stops of the light at the middle. Look, not data. */
const CORE_STOPS: readonly [number, string][] = [
  [0, 'rgba(238,246,255,.4)'],
  [0.1, 'rgba(226,238,255,.3)'],
  [0.22, 'rgba(206,224,255,.2)'],
  [0.36, 'rgba(178,204,246,.125)'],
  [0.52, 'rgba(148,180,232,.075)'],
  [0.68, 'rgba(120,155,214,.042)'],
  [0.84, 'rgba(98,132,196,.02)'],
  [0.94, 'rgba(84,116,180,.007)'],
  [1, 'rgba(0,0,0,0)'],
];

/**
 * Draw the ladder.
 *
 * The centre is projected ONCE and every rung is drawn around it, because a rung is a circle about
 * the world's origin: only its radii change with the projection, never where it is centred.
 */
export function drawRungs(ctx: DrawContext, view: View, world: RungWorld): void {
  const centre = toScreen(view, 0, 0);
  const z = view.cam.z;
  const alpha = rungAlpha(z).toFixed(3);
  for (const rung of world.rungs) {
    const rx = rung.r * z;
    /* Squashed and tilted on the way out, so a rung lies in the world's plane rather than facing
       the reader — which is what makes the tilt read as depth at all. */
    const ry = rung.r * world.squash * view.vt * z;
    if (rx < RUNG_MIN_PX) continue;
    ctx.strokeStyle = `rgba(146,168,214,${alpha})`;
    ctx.lineWidth = 1;
    ctx.setLineDash(RUNG_DASH);
    ctx.beginPath();
    ctx.ellipse(centre.x, centre.y, rx, ry, 0, 0, TAU);
    ctx.stroke();
    ctx.setLineDash(NO_DASH);
  }
  /* The glow is drawn whether or not any rung survived the cull: at a wide zoom it is the only mark
     of where the ladder starts, which is exactly when every rung has been culled. */
  const radius = Math.max(CORE_MIN_PX, world.coreRadius * z);
  const g = ctx.createRadialGradient(centre.x, centre.y, 0, centre.x, centre.y, radius);
  for (const [at, colour] of CORE_STOPS) g.addColorStop(at, colour);
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(centre.x, centre.y, radius, 0, TAU);
  ctx.fill();
}
